import { MenuList } from "@mui/material";
import SimplePopper from "../Poppers/SimplePopper";
import VirtualList from "./VirtualList";
import clsx from "clsx";
import listStyles from "./virtuallist.module.css";
import React, { useEffect } from "react";

interface PopUpVirtualListProps {
  open: boolean;
  inputRef: React.MutableRefObject<any>;
  anchorEl: React.MutableRefObject<any>;
  virtualList: React.MutableRefObject<any>;
  items: any[];
  itemSize: number;
  inputValue: any;
  value: any;
  preventDefaultKeyDownEvent?: boolean;
  topShift?: number;
  leftShift?: number;
  widthSift?: number;
  minWidthSift?: number;
  onClose: (ev?: any) => void;
  onSelect: (data?: any) => void;
  onKeyDown?: (ev: React.KeyboardEvent<HTMLDivElement>) => void;
}

const getHeight = (itemSize: number, items: any[]): number => {
  const length = items.length;

  return length * itemSize > 250 ? 250 : length * itemSize;
};

export default function PopupVirtualList({
  open,
  inputRef = { current: null },
  anchorEl,
  itemSize = 21,
  items = [],
  onClose = () => {},
  onSelect = () => {},
  onKeyDown,
  preventDefaultKeyDownEvent = false,
  value,
  inputValue,
  virtualList,
  topShift,
  leftShift,
  widthSift,
  minWidthSift,
}: PopUpVirtualListProps) {
  useEffect(() => {
    if (open) {
      inputRef.current.focus();
    }
    inputRef.current.onkeydown = onKeyDownInner;
  }, [open, onKeyDown]);

  const onKeyDownInner = (ev: React.KeyboardEvent<HTMLDivElement>) => {
    if (open) ev.stopPropagation();
    const keys: { [key: string]: (ev: React.KeyboardEvent<HTMLDivElement>) => void } = {
      NumpadEnter: onKeyEnter,
      Enter: onKeyEnter,
      Escape: onKeyEscape,
      ArrowUp: onKeyArrow,
      ArrowDown: onKeyArrow,
    };

    if (!preventDefaultKeyDownEvent && open) keys[ev.code]?.(ev);
    if (!open) onKeyDown?.(ev);
  };

  const onKeyArrow = (ev: React.KeyboardEvent<HTMLDivElement>) => {
    ev.preventDefault();

    const focusItem = virtualList.current?.querySelector(`.${clsx(listStyles.listItemFocus)}`);
    if (focusItem) {
      const next = ev.code === "ArrowDown" ? focusItem.nextElementSibling : focusItem.previousElementSibling;
      if (next) {
        focusItem.classList.remove(clsx(listStyles.listItemFocus));
        next.classList.add(clsx(listStyles.listItemFocus));
        next.focus();
        inputRef.current.focus();
      }
      return;
    }

    const selected = virtualList.current?.querySelector(`.${clsx(listStyles.listItemSelected)}`);
    if (selected) {
      const next = ev.code === "ArrowDown" ? selected.nextElementSibling : selected.previousElementSibling;
      if (next) {
        next.classList.add(clsx(listStyles.listItemFocus));
        next.focus();
        inputRef.current.focus();
      }
      return;
    }

    const firstItem = virtualList.current?.firstChild;
    if (firstItem) {
      const next = ev.code === "ArrowDown" ? firstItem.nextElementSibling : firstItem;
      next.classList.add(clsx(listStyles.listItemFocus));
      next.focus();
      inputRef.current.focus();
    }

    return;
  };

  const onKeyEnter = () => {
    const focused = virtualList.current.querySelector(`.${clsx(listStyles.listItemFocus)}`);
    if (focused) {
      focused?.click();
      inputRef.current.focus();
      return;
    }

    const selected = virtualList.current.querySelector(`.${clsx(listStyles.listItemSelected)}`);
    if (selected) {
      selected?.click();
      inputRef.current.focus();
      return;
    }
  };

  const onKeyEscape = () => {
    onClose({ target: null });
  };

  return (
    <SimplePopper
      onKeyDown={onKeyDownInner}
      open={open}
      anchorElRef={anchorEl}
      topShift={topShift}
      leftShift={leftShift}
      widthSift={widthSift}
      minWidthSift={minWidthSift}
      onClose={onClose}
      height={getHeight(itemSize, items)}
    >
      <MenuList
        tabIndex={-1}
        style={{
          height: `${getHeight(itemSize, items)}px`,
          maxHeight: "240px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          padding: "0",
          paddingRight: "0px",
          paddingLeft: "3px",
          backgroundColor: "#ffffff",
        }}
      >
        <VirtualList
          onKeyDown={(_data, e) => onKeyDownInner(e)}
          estimateSize={() => itemSize}
          refList={virtualList}
          noFocus
          elementToFocusRef={inputRef}
          items={items}
          selected={value}
          filterValue={inputValue}
          onClick={onSelect}
        />
      </MenuList>
    </SimplePopper>
  );
}
